import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft,
  Check,
  Crown,
  Zap,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchPlans();
    checkPaymentStatus();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await subscriptionAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    setProcessingPayment(true);
    
    // Poll for payment status
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setPaymentStatus('timeout');
        setProcessingPayment(false);
        return;
      }

      try {
        const response = await subscriptionAPI.getCheckoutStatus(sessionId);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          toast.success('Payment successful! Welcome to Pro!');
          await checkAuth(); // Refresh user data
          setProcessingPayment(false);
        } else if (response.data.status === 'expired') {
          setPaymentStatus('failed');
          toast.error('Payment session expired');
          setProcessingPayment(false);
        } else {
          // Continue polling
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        console.error('Payment status error:', error);
        setPaymentStatus('error');
        setProcessingPayment(false);
      }
    };

    pollStatus();
  };

  const handleSubscribe = async (planId) => {
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.createCheckout(planId, originUrl);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
    }
  };

  const isPro = user?.subscription_status === 'pro';

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading font-semibold text-xl text-white">Subscription</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-8">
        <div className="max-w-4xl mx-auto">
          {/* Payment Status */}
          {processingPayment && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <Card className="bg-[#0A0A0A] border-[#CCFF00]/30 p-6 rounded-2xl">
                <div className="flex items-center gap-4">
                  <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                  <div>
                    <h3 className="font-heading font-semibold text-white">Processing payment...</h3>
                    <p className="text-white/60 text-sm">Please wait while we confirm your payment</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {paymentStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <Card className="bg-green-500/10 border-green-500/30 p-6 rounded-2xl">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="font-heading font-semibold text-white">Payment successful!</h3>
                    <p className="text-white/60 text-sm">Welcome to DIDIT Pro. Enjoy unlimited video editing!</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Current Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isPro ? 'bg-[#CCFF00]/20' : 'bg-white/10'
                  }`}>
                    <Crown className={`w-6 h-6 ${isPro ? 'text-[#CCFF00]' : 'text-white/40'}`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white">
                      Current Plan: {isPro ? 'Pro' : 'Free'}
                    </h3>
                    <p className="text-white/60 text-sm">
                      {isPro 
                        ? user.subscription_expires 
                          ? `Expires: ${new Date(user.subscription_expires).toLocaleDateString()}`
                          : 'Active subscription'
                        : 'Upgrade to unlock all features'
                      }
                    </p>
                  </div>
                </div>
                {isPro && (
                  <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#CCFF00] rounded-full text-sm font-medium">
                    Active
                  </span>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-[#0A0A0A] border-white/10 p-8 rounded-2xl h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-2xl text-white">Monthly</h3>
                  <Zap className="w-6 h-6 text-[#CCFF00]" />
                </div>
                
                <div className="mb-6">
                  <span className="font-marketing font-bold text-5xl text-white">CHF 15</span>
                  <span className="text-white/60">/month</span>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {['Unlimited projects', 'AI captions', 'Premium templates', 'Priority processing', '1080p export'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#CCFF00]" />
                      <span className="text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-xl font-semibold py-6 ${
                    isPro 
                      ? 'bg-white/10 text-white/60 cursor-not-allowed' 
                      : 'bg-[#CCFF00] text-black hover:bg-[#B3E600]'
                  }`}
                  onClick={() => !isPro && handleSubscribe('monthly')}
                  disabled={isPro}
                  data-testid="subscribe-monthly-btn"
                >
                  {isPro ? 'Current Plan' : 'Subscribe Monthly'}
                </Button>
              </Card>
            </motion.div>

            {/* Annual Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-[#CCFF00] border-[#CCFF00] p-8 rounded-2xl h-full flex flex-col relative overflow-hidden">
                <div className="absolute top-4 right-4 px-3 py-1 bg-black/20 rounded-full">
                  <span className="text-black text-sm font-semibold">Save 20%</span>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-2xl text-black">Annual</h3>
                  <Crown className="w-6 h-6 text-black" />
                </div>
                
                <div className="mb-6">
                  <span className="font-marketing font-bold text-5xl text-black">CHF 144</span>
                  <span className="text-black/60">/year</span>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {['Everything in Monthly', '2 months free', '4K export', 'API access', 'Priority support'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-black" />
                      <span className="text-black/80">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-xl font-semibold py-6 ${
                    isPro 
                      ? 'bg-black/20 text-black/60 cursor-not-allowed' 
                      : 'bg-black text-white hover:bg-black/80'
                  }`}
                  onClick={() => !isPro && handleSubscribe('annual')}
                  disabled={isPro}
                  data-testid="subscribe-annual-btn"
                >
                  {isPro ? 'Current Plan' : 'Subscribe Annually'}
                </Button>
              </Card>
            </motion.div>
          </div>

          {/* Features Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">
              Compare Plans
            </h2>
            
            <Card className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-white/60">Feature</th>
                    <th className="text-center p-4 text-white/60">Free</th>
                    <th className="text-center p-4 text-[#CCFF00]">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Projects', '2', 'Unlimited'],
                    ['Export Quality', '720p', 'Up to 4K'],
                    ['AI Captions', 'Basic', 'Advanced'],
                    ['Processing Speed', 'Standard', 'Priority'],
                    ['Templates', 'Basic', 'Premium'],
                    ['Support', 'Community', 'Priority'],
                  ].map(([feature, free, pro], i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-4 text-white">{feature}</td>
                      <td className="p-4 text-center text-white/60">{free}</td>
                      <td className="p-4 text-center text-white">{pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Subscription;
